//@ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Button,
  TextField,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { styled } from "@mui/system";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import api from "../../utils/Url";
import CustomLabel from "../../CustomLable";

/* -------------------- Styles -------------------- */
const containerStyle = {
  width: "100%",
  height: "500px",
};

const StyledRadioGroup = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "10px",
  backgroundColor: "#f4f6f8",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
}));

/* -------------------- Component -------------------- */
export default function Tracking() {
  const { t } = useTranslation();

  /* -------------------- State -------------------- */
  const [vehicleTypes, setVehicleTypes] = useState<any[]>([]);
  const [vehicleNos, setVehicleNos] = useState<any[]>([]);
  const [vType, setVType] = useState<number[]>([]);
  const [vNo, setVNo] = useState<number[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [activeVehicle, setActiveVehicle] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [center, setCenter] = useState({
    lat: 26.447965,
    lng: 80.3432,
  });

  const mapRef = useRef<any>(null);

  /* -------------------- Initial Load -------------------- */
  useEffect(() => {
    fetchVehicleTypes();
    fetchVehicleNos();
    fetchTrackingData();
  }, []);

  /* -------------------- APIs -------------------- */

  const fetchVehicleTypes = async () => {
    const res = await api.get(
      "Rep/GetVehicleType"
    );

    setVehicleTypes(
      res.data.data.map((x: any) => ({
        label: x.vehicleType || "NA",
        value: x.itemTypeid,
      }))
    );
  };

  const fetchVehicleNos = async () => {
    const res = await api.get(
      "Rep/GetVehiclesm"
    );

    setVehicleNos(
      res.data.data.map((x: any) => ({
        label: x.vehicleNo,
        value: x.itemMasterid,
      }))
    );
  };

  const fetchTrackingData = async () => {
    setLoading(true);

    const payload = {
      itemMasterids: vNo.length ? vNo : [null],
      itemTypeids: vType.length ? vType : [null],
      forDate: null,
    };

    const res = await api.post(
      "Rep/GetVehicleTrack",
      payload
    );

    const mapped = res.data.data.map((x: any, index: number) => ({
      id: index,
      vehicleNo: x.vehicleNo || "NA",
      vehicleTypename: x.vehicleType || "NA",
      lat: x.latitude,
      lng: x.longitude,
      speed: x.speed ?? 0,
      empName: x.driver || "NA",
      departmentname: x.dept || "NA",
      tracktime: new Date(x.trackTime).toLocaleString(),
      icon:
        x.speed === 0
          ? "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-red-truck-transportation-png-image_11507611.png"
          : "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-green-truck-transport-png-image_11508688.png",
    }));

    setVehicles(mapped);
    adjustMap(mapped);
    setLoading(false);
  };

  /* -------------------- Map Helpers -------------------- */
  const adjustMap = (list: any[]) => {
    if (!list.length || !mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    list.forEach((v) =>
      bounds.extend(new window.google.maps.LatLng(v.lat, v.lng))
    );

    mapRef.current.fitBounds(bounds);

    const avgLat =
      list.reduce((s, v) => s + v.lat, 0) / list.length;
    const avgLng =
      list.reduce((s, v) => s + v.lng, 0) / list.length;

    setCenter({ lat: avgLat, lng: avgLng });
  };

  /* -------------------- UI -------------------- */
  return (
    <Grid container spacing={2}>
      {/* Vehicle Type */}
      <Grid item md={5}>
        <StyledRadioGroup>
          <Autocomplete
            multiple
            options={vehicleTypes}
            disableCloseOnSelect
            onChange={(e, v) => setVType(v.map((x: any) => x.value))}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} />
                <ListItemText primary={option.label} />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={<CustomLabel text={t("text.SelectVehicleType")} />}
              />
            )}
          />
        </StyledRadioGroup>
      </Grid>

      {/* Vehicle No */}
      <Grid item md={5}>
        <StyledRadioGroup>
          <Autocomplete
            multiple
            options={vehicleNos}
            disableCloseOnSelect
            onChange={(e, v) => setVNo(v.map((x: any) => x.value))}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} />
                <ListItemText primary={option.label} />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={<CustomLabel text={t("text.SelectVehicleNo")} />}
              />
            )}
          />
        </StyledRadioGroup>
      </Grid>

      {/* Show Button */}
      <Grid item md={2}>
        <StyledRadioGroup>
          <Button
            fullWidth
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={18} /> : <VisibilityIcon />
            }
            style={{
              backgroundColor: "var(--header-background)",
              color: "#fff",
            }}
            onClick={fetchTrackingData}
          >
            {loading ? "Loading..." : "Show"}
          </Button>
        </StyledRadioGroup>
      </Grid>

      {/* Map */}
      <Grid item xs={12}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={12}
          onLoad={(map) => (mapRef.current = map)}
        >
          {vehicles.map((v) => (
            <Marker
              key={v.id}
              position={{ lat: v.lat, lng: v.lng }}
              icon={{
                url: v.icon,
                scaledSize: new window.google.maps.Size(44, 44),
              }}
              onMouseOver={() => setActiveVehicle(v)}
              onMouseOut={() => setActiveVehicle(null)}
            >
              {activeVehicle?.id === v.id && (
                <InfoWindow>
                  <div style={{ minWidth: 220 }}>
                    <b>Vehicle:</b> {v.vehicleNo} <br />
                    <b>Type:</b> {v.vehicleTypename} <br />
                    <b>Speed:</b> {v.speed} <br />
                    <b>Department:</b> {v.departmentname} <br />
                    <b>Driver:</b> {v.empName} <br />
                    <b>Date & Time:</b> {v.tracktime}
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ))}
        </GoogleMap>
      </Grid>
    </Grid>
  );
}




















// import React, { useEffect, useRef, useState } from "react";
// import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import {
//   Card,
//   Grid,
//   CardContent,
//   Typography,
//   Divider,
//   Switch,
//   Button,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   TextField,
//   DialogActions,
//   Stack,
//   Paper,
//   CircularProgress,
//   Select,
//   MenuItem,
//   Radio,
//   FormLabel,
//   Autocomplete,
//   Checkbox,
//   ListItemText,
//   RadioGroup,
// } from "@mui/material";
// import { useTranslation } from "react-i18next";
// import api from "../../utils/Url";
// import Box from "@mui/material/Box";

// import { toast } from "react-toastify";
// import {
//   GoogleMap,
//   InfoWindow,
//   LoadScript,
//   Marker,
// } from "@react-google-maps/api";
// import CustomLabel from "../../CustomLable";
// import { styled } from "@mui/system";
// import VisibilityIcon from "@mui/icons-material/Visibility";

// const containerStyle = {
//   width: "100%",
//   height: "500px",
// };

// const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
//   display: "flex",
//   flexDirection: "column",
//   padding: theme.spacing(2),
//   borderRadius: "8px",
//   boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
//   backgroundColor: "#e3e3e3", // Optional: set a background color
// }));

// interface MenuPermission {
//   isAdd: boolean;
//   isEdit: boolean;
//   isPrint: boolean;
//   isDel: boolean;
// }

// export default function Tracking() {
//   const { t } = useTranslation();

//   const [isType, setType] = useState<any>([
//     { value: "-1", label: t("text.SelectVehicleType") },
//   ]);

//   const [vType, setVType] = useState<any>([]);

//   const [vNo, setVNo] = useState<any>([]);

//   const [VehicleNos, setVehicleNos] = useState<any>([
//     { value: "-1", label: t("text.SelectVehicleNo") },
//   ]);



//   const navigate = useNavigate();

//   useEffect(() => {
//     getVehicleType();
//     getVehicleNo();
//     getDashData();

//   }, []);

//   const getVehicleType = () => {
//     const collectData = {
//       vehicleTypeId: -1,
//       vehicleTypename: "",
//     };
//     api.post(`VehicleType/GetVehicleType`, collectData).then((res) => {
//       const arr = res?.data?.data.map((item: any) => ({
//         label: item.vehicleTypename,
//         value: item.vehicleTypeId,
//       }));
//       setType(arr);
//     });
//   };



//   // const getVehicleNo = () => {
//   //   const collectData = {
//   //     vehicleTypeId: -1,
//   //     vehicleTypename: "",
//   //   };
//   //   api.post(`VehicleType/GetVehicleType`, collectData).then((res) => {
//   //     const arr = res?.data?.data.map((item: any) => ({
//   //       label: item.vehicleTypename,
//   //       value: item.vehicleTypeId,
//   //     }));
//   //     setVehicleNos(arr);
//   //   });
//   // };

//   // const center = {
//   //   lat: 34.0522,
//   //   lng: 118.2437,}
//   const formatTrackTime = (tracktime: string) => {
//     const trackTime = new Date(tracktime);

//     const day = trackTime.getDate().toString().padStart(2, "0");
//     const month = (trackTime.getMonth() + 1).toString().padStart(2, "0");
//     const year = trackTime.getFullYear();
//     const hours = trackTime.getHours().toString().padStart(2, "0");
//     const minutes = trackTime.getMinutes().toString().padStart(2, "0");
//     const seconds = trackTime.getSeconds().toString().padStart(2, "0");

//     return `${day}-${month}-${year} (Time:- ${hours}:${minutes}:${seconds})`;
//   };



//   const [vehicles, setVehicles] = useState<any>([]);
//   const [activeVehicle, setActiveVehicle] = useState<any>(null);
//   const [center, setCenter] = useState({ lat: 26.447965, lng: 80.3432 });
//   const mapRef: any = useRef(null);

//   // Fetch the vehicle data from API
//   const getDashData = () => {
//     const collectData = {
//       fencId: null,
//       fenceName: null,
//       lsVehType: vType ? vType : [],
//       lsVehNos:vNo ? vNo : [],
//       dateSaveFr: new Date().toISOString(),
//       dateSaveTo: new Date().toISOString(),
//     };

//     api.post(`NTRead/GetDashData`, collectData).then((res) => {
//       const arr = res?.data?.data?.listNTSumm.map((item: any) => ({
//         vehicleNo: item.vehicleno || "No ID",
//         lat: item.lattitude,
//         lng: item.longitude,
//         id: item.id,
//         speed: item.speed,
//         tracktime: formatTrackTime(item.tracktime),
//         departmentname: item.departmentname,
//         empName: item.empName,
//         vehicleTypename: item.vehicleTypename,
//         icon: item.speed === 0
//             ? "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-red-truck-transportation-png-image_11507611.png"
//             : "https://png.pngtree.com/png-vector/20240128/ourmid/pngtree-green-truck-transport-png-image_11508688.png",
//       }));
//       setVehicles(arr);
//       setCenter(calculateCenter(arr));

//       if (arr.length > 0) {
//         adjustZoomLevel(arr);
//       }
//     });
//   };

//   const calculateCenter = (vehicles: any) => {
//     if (vehicles.length === 0) {
//       return { lat: 26.447965, lng: 80.3432 }; // Default center if no vehicles
//     }

//     const latitudes = vehicles.map((vehicle: any) => vehicle.lat);
//     const longitudes = vehicles.map((vehicle: any) => vehicle.lng);

//     const avgLat =
//       latitudes.reduce((sum: any, lat: any) => sum + lat, 0) / latitudes.length;
//     const avgLng =
//       longitudes.reduce((sum: any, lng: any) => sum + lng, 0) /
//       longitudes.length;

//     return { lat: avgLat, lng: avgLng }; // Return the center point
//   };

//   // Adjust the zoom level based on the spread of the vehicles
//   const adjustZoomLevel = (vehicles: any) => {
//     const bounds = new window.google.maps.LatLngBounds();

//     vehicles.forEach((vehicle: any) => {
//       bounds.extend(new window.google.maps.LatLng(vehicle.lat, vehicle.lng));
//     });

//     if (mapRef.current) {
//       mapRef.current.fitBounds(bounds); // Fit the map to the bounds of all vehicles
//     }
//   };

//   const handleMarkerMouseOver = (vehicle: any) => {
//     setActiveVehicle(vehicle);
//   };

//   const handleMarkerMouseOut = () => {
//     setActiveVehicle(null);
//   };

//   const getVehicleNo = () => {
//     const collectData = {
//       orderby: "",
//       pageNo: 0,
//       pageSize: 0,
//       intnotnullvalue1: 0,
//       userId: "",

//       str1: "",
//       str2: "",
//       str3: "",
//       str4: "",
//       str5: "",
//       intvalue1: 0,
//       intvalue2: 0,
//       intvalue3: 0,
//       intvalue4: 0,
//       intnotnullvalue2: 0,
//       intnotnullvalue3: 0,
//       date1:new Date().toISOString(),
//       date2: new Date().toISOString(),
//       date3: new Date().toISOString(),
//       date4:new Date().toISOString(),
//       dec1: 0,
//       dec2: 0,
//       dec3: 0,
//       dec4: 0,
//       flag: true,
//       data: "",
//       success: true,
//       error: "",
//       selectPerindex: 0,
//       show: true,
//     };
//     api.post(`Dashboard/GetvVehicleNo`, collectData).then((res) => {
//       const arr = res?.data?.data.map((item: any) => ({
//         label: item.vehicleNo,
//         value: item.vehicleNo,
//       }));
//       setVehicleNos(arr);
//     });
//   };

//   return (
//     <div>
//       <Grid
//         container
//         spacing={2}
//         style={{
//           // backgroundColor:'red',
//           //marginTop: "2vh",
//           //marginTop: "2vh",
//         }}
//       >
//         <Grid item md={5}>
//           <StyledRadioGroup>
//             <Autocomplete
//               multiple
//               disablePortal
//               id="combo-box-demo"
//               options={isType}
//               onChange={(event: any, newValue: any) => {
//                 const selectedValues = newValue.map((item: any) => item.value);
//                 //console.log(selectedValues);
//                 setVType(selectedValues);

//                 //getDashData(selectedValues);
//               }}
//               fullWidth
//               size="small"
//               disableCloseOnSelect
//               style={{ backgroundColor: "#ffff" }}
//               renderOption={(props, option, { selected }) => (
//                 <li {...props}>
//                   <Checkbox style={{ marginRight: 8 }} checked={selected} />
//                   <ListItemText primary={option.label} />
//                 </li>
//               )}
//               renderInput={(params: any) => (
//                 <TextField
//                   {...params}
//                   label={<CustomLabel text={t("text.SelectVehicleType")} />}
//                 />
//               )}
//             />
//           </StyledRadioGroup>
//         </Grid>

//         <Grid item md={5}>
//           <StyledRadioGroup>
//             <Autocomplete
//               multiple
//               disablePortal
//               id="combo-box-demo"
//               options={VehicleNos}
//               //getOptionLabel={(option: any) => option.label}
//               // value={
//               //     ZoneOption.find(
//               //         (option) => option.value === formik.values.fileTypeId
//               //     ) || null
//               // }
//               onChange={(event: any, newValue: any) => {
//                 console.log(newValue?.value);

//                 const selectedValues = newValue.map((item: any) => item.value);
//                 //console.log(selectedValues);
//                 setVNo(selectedValues);

//               }}
//               fullWidth
//               size="small"
//               disableCloseOnSelect
//               style={{ backgroundColor: "#ffff" }}
//               renderOption={(props, option, { selected }) => (
//                 <li {...props}>
//                   <Checkbox style={{ marginRight: 8 }} checked={selected} />
//                   <ListItemText primary={option.label} />
//                 </li>
//               )}
//               renderInput={(params: any) => (
//                 <TextField
//                   {...params}
//                   label={<CustomLabel text={t("text.SelectVehicleNo")} />}
//                 />
//               )}
//             />
//           </StyledRadioGroup>
//         </Grid>

//         <Grid xs={12} sm={2} md={2} item>
//           <StyledRadioGroup>
//             <Button
//               type="submit"
//               fullWidth
//               style={{
//                 backgroundColor: `var(--header-background)`,
//                 color: "white",
//               }}
//               onClick={() => {
//                 getDashData();
//               }}
//               startIcon={<VisibilityIcon />}
//             >
//               Show
//             </Button>
//           </StyledRadioGroup>
//         </Grid>

//         <Grid item xs={12} sm={12} lg={12}>
//           <div
//             style={{
//               border: "1px solid black",
//               boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
//             }}
//           >
//             {/* <LoadScript googleMapsApiKey="AIzaSyDxp8MamOh_0gBNHBq4m3bgCIgksc4YYgQ"> */}
//               <GoogleMap
//                 mapContainerStyle={containerStyle}
//                 center={center}
//                 zoom={70} // Default zoom, will be adjusted by `fitBounds`
//                 onLoad={(map: any) => (mapRef.current = map)}
//               >
//                 {vehicles.map((vehicle: any) => (
//                   <Marker
//                     key={vehicle.id}
//                     position={{ lat: vehicle.lat, lng: vehicle.lng }}
//                     icon={{
//                       url: vehicle.icon,
//                       scaledSize: new window.google.maps.Size(50, 50),
//                       origin: new window.google.maps.Point(0, 0),
//                       anchor: new window.google.maps.Point(25, 25),
//                     }}
//                     onMouseOver={() => handleMarkerMouseOver(vehicle)} // Show vehicle number on hover
//                     onMouseOut={handleMarkerMouseOut} // Hide info window on hover out
//                   >
//                     {activeVehicle && activeVehicle.id === vehicle.id && (
//                       <InfoWindow>
//                         <div>
//                           <div>
//                             <strong>Vehicle No: </strong>
//                             {vehicle.vehicleNo}
//                           </div>

//                           <div>
//                             <strong>vehicle Type: </strong>
//                             {vehicle.vehicleTypename}
//                           </div>

//                           <div>
//                             <strong>Speed: </strong>
//                             {vehicle.speed}
//                           </div>

//                           <div>
//                             <strong>Department: </strong>
//                             {vehicle.departmentname}
//                           </div>

//                           <div>
//                             <strong>Date & Time: </strong>
//                             {vehicle.tracktime}
//                           </div>

//                           <div>
//                             <strong>Driver: </strong>
//                             {vehicle.empName}
//                           </div>
//                         </div>
//                       </InfoWindow>
//                     )}
//                   </Marker>
//                 ))}
//               </GoogleMap>
//             {/* </LoadScript> */}
//           </div>
//         </Grid>
//       </Grid>
//     </div>
//   );
// }
